import type { ArgField, ArgType, InstructionMeta, ProgramMeta } from "./types";

/** Shape of an Anchor IDL instruction arg type */
type IdlArgType =
  | string
  | { option: IdlArgType }
  | { defined: { name: string } }
  | { vec: IdlArgType }
  | { array: [IdlArgType, number] };

interface IdlArg {
  name: string;
  type: IdlArgType;
}

interface IdlInstruction {
  name: string;
  docs?: string[];
  discriminator: number[];
  args: IdlArg[];
}

interface IdlJson {
  address: string;
  metadata: { name: string };
  instructions: IdlInstruction[];
}

/** Primitive type strings that map directly to ArgType */
const PRIMITIVE_TYPES = new Set<string>([
  "u8",
  "u16",
  "u32",
  "u64",
  "i64",
  "u128",
  "bool",
  "pubkey",
  "string",
  "bytes",
]);

function snakeToTitleCase(snake: string): string {
  return snake
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function resolveArgType(idlType: IdlArgType): {
  type: ArgType | null;
  optional: boolean;
  isComplex: boolean;
} {
  // Simple primitive string
  if (typeof idlType === "string") {
    if (PRIMITIVE_TYPES.has(idlType)) {
      return { type: idlType as ArgType, optional: false, isComplex: false };
    }
    // Unknown primitive string — treat as complex
    return { type: null, optional: false, isComplex: true };
  }

  // Option<T>
  if ("option" in idlType) {
    const inner = resolveArgType(idlType.option);
    return { ...inner, optional: true };
  }

  // Defined struct / Vec / Array — mark complex
  if ("defined" in idlType || "vec" in idlType || "array" in idlType) {
    return { type: null, optional: false, isComplex: true };
  }

  return { type: null, optional: false, isComplex: true };
}

function parseInstruction(ix: IdlInstruction): InstructionMeta {
  const args: ArgField[] = [];
  let hasComplexArgs = false;

  for (const arg of ix.args) {
    const resolved = resolveArgType(arg.type);

    if (resolved.isComplex || resolved.type === null) {
      hasComplexArgs = true;
      continue;
    }

    args.push({
      name: arg.name,
      label: snakeToTitleCase(arg.name),
      type: resolved.type,
      optional: resolved.optional,
    });
  }

  return {
    name: ix.name,
    displayName: snakeToTitleCase(ix.name),
    description: ix.docs?.[0] ?? "",
    discriminator: ix.discriminator,
    args,
    hasComplexArgs,
  };
}

export function discriminatorToHex(disc: number[]): string {
  return disc.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function parseIdl(idl: IdlJson): ProgramMeta {
  const instructions = new Map<string, InstructionMeta>();

  for (const ix of idl.instructions) {
    const meta = parseInstruction(ix);
    const hex = discriminatorToHex(ix.discriminator);
    instructions.set(hex, meta);
  }

  return {
    programId: idl.address,
    name: idl.metadata.name,
    displayName: snakeToTitleCase(idl.metadata.name),
    instructions,
  };
}
